import { LazyMotion } from "framer-motion";
import { memo, useEffect } from "react";
import { useLocal } from "../../util/local.ts";
import { CommandPalette } from "../CommandPalette/index.tsx";
import { GmShell } from "./GmShell.tsx";
import { Notifications } from "./Notifications.tsx";
import { useViewData } from "../../util/viewManager.tsx";
import api from "../../api/index.ts";
import { ErrorBoundary } from "../ErrorBoundary.tsx";

const loadFramerMotionFeatures = () =>
	import("../../util/framerMotionFeatures.ts").then((res) => res.default);

type KeepPreviousRenderWhileUpdatingProps = {
	children: any;
	updating: boolean;
};
const KeepPreviousRenderWhileUpdating = memo(
	(props: KeepPreviousRenderWhileUpdatingProps) => {
		return props.children;
	},
	(
		prevProps: KeepPreviousRenderWhileUpdatingProps,
		nextProps: KeepPreviousRenderWhileUpdatingProps,
	) => {
		// No point in rendering while updating contents
		return nextProps.updating;
	},
);

export const Controller = () => {
	const state = useViewData();

	const { popup } = useLocal(["popup"]);

	useEffect(() => {
		if (popup) {
			document.body.style.paddingTop = "8px";
			const css = document.createElement("style");
			css.innerHTML = ".new_window { display: none }";
			document.body.append(css);
		}
	}, [popup]);

	useEffect(() => {
		// Try to show ads on initial render
		api.initAds("uiRendered");
	}, []);

	const {
		Component,
		data,
		idLoaded,
		inLeague,
		loading: updating,
		scrollToTop,
	} = state;

	// Scroll to top if this load came from user clicking a link to a new page
	useEffect(() => {
		if (scrollToTop) {
			window.scrollTo(window.pageXOffset, 0);
		}
	}, [idLoaded, scrollToTop]);

	// Mobile GM frontend: ZenGM's chrome (NavBar/SideBar/TitleBar/Header/LeagueTopBar/Footer/
	// Skyscraper) is replaced by GmShell (dark canvas + bottom tab nav). Only the current view,
	// toasts (Notifications), and the command palette are kept.
	return (
		<LazyMotion strict features={loadFramerMotionFeatures}>
			<GmShell inLeague={inLeague}>
				<main id="actual-actual-content" className="clearfix">
					<ErrorBoundary key={idLoaded}>
						{Component ? (
							<KeepPreviousRenderWhileUpdating updating={updating}>
								<Component {...data} />
							</KeepPreviousRenderWhileUpdating>
						) : null}
					</ErrorBoundary>
				</main>
				<CommandPalette />
			</GmShell>
			<Notifications />
		</LazyMotion>
	);
};
